import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TemplateGallery = ({ categories, onTemplateSelect, currentTemplate }) => {
  const [selectedCategory, setSelectedCategory] = useState(categories?.[0]?.id || 'literary-fiction');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  const filteredCategories = categories?.filter(category =>
    category?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    category?.templates?.some(template =>
      template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    )
  );

  const currentCategoryTemplates = categories?.find(cat => cat?.id === selectedCategory)?.templates || [];

  const handleTemplateSelect = (template) => {
    const enhancedTemplate = {
      ...template,
      category: selectedCategory,
      aiEnhanced: true,
      responsive: true,
      accessibility: 'AAA'
    };
    onTemplateSelect?.(enhancedTemplate);
  };

  return (
    <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
      {/* Gallery Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">Template Gallery</h3>
          <div className="flex items-center space-x-1 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            >
              <Icon name="Grid3X3" size={14} className="text-slate-600" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              <Icon name="List" size={14} className="text-slate-600" />
            </button>
          </div>
        </div>
        
        {/* Search Templates */}
        <div className="relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>
      {/* Category Tabs */}
      <div className="p-4 border-b border-slate-200 space-y-2">
        {filteredCategories?.map((category) => (
          <button
            key={category?.id}
            onClick={() => setSelectedCategory(category?.id)}
            className={`w-full text-left p-3 rounded-lg transition-colors ${
              selectedCategory === category?.id
                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' :'hover:bg-slate-50 text-slate-600'
            }`}
          >
            <div className="font-medium text-sm">{category?.name}</div>
            <div className="text-xs text-slate-500 mt-1">{category?.description}</div>
            <div className="text-xs text-slate-400 mt-1">
              {category?.templates?.length} templates
            </div>
          </button>
        ))}
      </div>
      {/* Template List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 gap-3' : 'space-y-2'}>
          {currentCategoryTemplates?.map((template) => (
            <div
              key={template?.id}
              onClick={() => handleTemplateSelect(template)}
              className={`cursor-pointer rounded-lg border-2 transition-all hover:shadow-md ${
                currentTemplate?.id === template?.id
                  ? 'border-indigo-500 bg-indigo-50' :'border-slate-200 hover:border-indigo-300'
              } ${viewMode === 'grid' ? 'p-3' : 'p-2'}`}
            >
              {viewMode === 'grid' ? (
                <>
                  {/* Template Preview */}
                  <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 rounded-md mb-3 overflow-hidden">
                    <img
                      src={template?.preview}
                      alt={`Preview of ${template?.name} template showing professional layout design`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Template Info */}
                  <div>
                    <h4 className="font-medium text-slate-900 text-sm mb-1">{template?.name}</h4>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="flex items-center space-x-1">
                        <Icon name="Sparkles" size={10} />
                        <span>AI Enhanced</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="Smartphone" size={10} />
                        <span>Responsive</span>
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded overflow-hidden">
                    <img
                      src={template?.preview}
                      alt={`Thumbnail of ${template?.name} template`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900 text-sm">{template?.name}</h4>
                    <div className="flex items-center space-x-2 text-xs text-slate-500">
                      <Icon name="Sparkles" size={10} />
                      <span>AI Enhanced</span>
                    </div>
                  </div>
                  <Icon name="ChevronRight" size={14} className="text-slate-400" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Empty State */}
        {currentCategoryTemplates?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Layout" size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No templates found</p>
            <p className="text-slate-400 text-xs mt-1">Try adjusting your search</p>
          </div>
        )}
      </div>
      {/* Template Actions */}
      <div className="p-4 border-t border-slate-200 space-y-2">
        <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
          <Icon name="Plus" size={16} />
          <span className="text-sm font-medium">Create Custom Template</span>
        </button>
        
        <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
          <Icon name="Upload" size={16} />
          <span className="text-sm font-medium">Import Template</span>
        </button>
      </div>
    </div>
  );
};

export default TemplateGallery;